import { useEffect, useRef } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { redeemMagicLink } from '@/api/portal';
import { toApiError } from '@/api/client';
import { PortalBrandHeader } from '@/components/PortalBrandHeader';

export interface AuthSearch {
  token?: string;
}

export const Route = createFileRoute('/portal/auth')({
  validateSearch: (search: Record<string, unknown>): AuthSearch => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: PortalAuth,
});

function PortalAuth(): JSX.Element {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const fired = useRef(false);

  const mutation = useMutation({
    mutationFn: (t: string) => redeemMagicLink(t),
    onSuccess: () => navigate({ to: '/portal' }),
  });

  // Canjea el token una sola vez al montar (el token es de un solo uso; un doble-fire lo quemaría).
  useEffect(() => {
    if (token && !fired.current) {
      fired.current = true;
      mutation.mutate(token);
    }
  }, [token, mutation]);

  return (
    <Box>
      <PortalBrandHeader branding={null} subtitle="Portal de clientes" />
      <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        {!token ? (
          <Stack spacing={2} alignItems="center" textAlign="center" sx={{ py: 2 }}>
            <Alert severity="error" sx={{ width: '100%' }}>
              El enlace de acceso es inválido o está incompleto.
            </Alert>
            <Button variant="contained" onClick={() => navigate({ to: '/portal/login' })}>
              Volver a ingresar
            </Button>
          </Stack>
        ) : mutation.isError ? (
          <Stack spacing={2} alignItems="center" textAlign="center" sx={{ py: 2 }}>
            <Alert severity="error" sx={{ width: '100%' }}>
              {toApiError(mutation.error).message || 'El enlace venció o ya fue usado.'}
            </Alert>
            <Button variant="contained" onClick={() => navigate({ to: '/portal/login' })}>
              Pedir un enlace nuevo
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center" textAlign="center" sx={{ py: 3 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Validando tu enlace de acceso…
            </Typography>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
